import React, { ElementType, MouseEvent, useEffect } from 'react';
import clsx from 'clsx';

export enum LinkVariant {
  primary = 'primary',
  secondary = 'secondary',
  secondaryInvert = 'secondaryInvert'
}

export type LinkProps<ComponentType extends React.ElementType> = {
  /**
   * Состояние неактивной ссылки. Компонент недоступен для действий пользователя
   */
  inactive?: boolean;
  /**
   * Применять ли фиксированные свойства шрифта.
   * Если не передать standalone, то свойства шрифта наследуются от родителя.
   */
  standalone?: boolean;
  /**
   * Вариант отображения компонента (`default`, `secondary`, `secondaryInvert`)
   */
  variant?: LinkVariant;
  /**
   * Подчеркивание ссылки
   */
  underlined?: boolean;
  /**
   * Выделение посещенной ссылки
   */
  showVisited?: boolean;
  /**
   * Тип компонента
   *
   * @type React.ElementType
   */
  component?: ComponentType;
  /**
   * Реф на корневой элемент
   */
  innerRef?: React.Ref<HTMLElement>;
} & Omit<React.ComponentProps<ComponentType>, 'inactive'>;

export function Link(props: LinkProps<'a'>): JSX.Element;
export function Link<ComponentType extends React.ElementType>(
  props: LinkProps<ComponentType>
): JSX.Element;
export function Link<ComponentType extends React.ElementType>({
  className,
  children,
  inactive,
  standalone,
  variant = LinkVariant.primary,
  underlined,
  showVisited,
  onClick,
  component,
  innerRef,
  ...props
}: LinkProps<ComponentType>) {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (inactive) e.preventDefault();
    onClick?.(e);
  };
  const Component = (component || 'a') as React.ElementType;

  useEffect(() => {
    if (variant === LinkVariant.primary && underlined !== undefined) {
      console.warn(
        'Использование primary варианта и свойства underlined не поддерживается'
      );
    }
  }, [variant, underlined]);

  return (
    <Component
      ref={innerRef}
      className={clsx(className, 'hse-Link', {
        'hse-Link_underlined': variant !== LinkVariant.primary && underlined,
        'hse-Link_showVisited': showVisited,
        'hse-Link_inactive': inactive,
        'hse-Link_standalone': standalone,
        [`hse-Link_variant_${variant}`]: variant
      })}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Component>
  );
}

Link.Variant = LinkVariant;
